<template>
  <v-alert
    v-if="error && !disableErrorMessage"
    type="warning"
    elevation="2"
    icon="warning"
    prominent
    class="ma-3"
  >
    {{ error }}
  </v-alert>
  <v-sheet v-else class="border-bottom">
    <div v-if="loading"></div>
    <v-slide-group center-active show-arrows v-model="selectedThumbnail" v-else>
      <v-slide-group-item
        v-for="(thumbnail, index) in thumbnails"
        :key="thumbnail.id"
        :value="thumbnail.id"
        v-slot="{ isSelected, toggle }"
      >
        <v-hover>
          <template v-slot:default="{ hover }">
            <v-card
              variant="text"
              class="mb-2 mt-2 ml-2 text-center rounded"
              :class="{
                'on-hover': hover,
                'selected-thumbnail': thumbnail.id === selectedThumbnail,
                'pinned-thumbnail':
                  thumbnail.id === pinnedThumbnail.id &&
                  thumbnail.id !== selectedThumbnail,
              }"
              width="80"
              @click="toggleCamera(thumbnail.id)"
            >
              <v-row align="start">
                <v-overlay
                  :absolute="true"
                  :value="hover"
                  :class="{ 'show-btns': hover }"
                  class="rounded"
                >
                  <v-icon color="var(--color-accent-blue)" size="24">
                    mdi-play-circle
                  </v-icon>
                </v-overlay>
                <v-overlay
                  :absolute="true"
                  :value="isSelected"
                  class="rounded video-thumbnail-image-border"
                >
                  <v-icon
                    v-if="select"
                    color="var(--color-accent-blue)"
                    size="24"
                  >
                    mdi-close-circle-outline
                  </v-icon>
                </v-overlay>
                <v-col class="pt-0 mt-3">
                  <!-- If thumbnail is found -->
                  <v-img
                    v-if="thumbnail.src && !thumbnail.loadingTimeout"
                    :src="thumbnail.src"
                    max-width="100"
                    height="50"
                    cover
                    @load="handleLoad(index)"
                    @error="handleLoadFailure(index)"
                  >
                    <template v-slot:placeholder>
                      <v-progress-circular
                        class="mt-2"
                        indeterminate
                        color="var(--color-accent-blue)"
                      ></v-progress-circular>
                    </template>
                  </v-img>
                  <!-- If thumbnail is not found -->
                  <v-img v-else class="rounded-top" max-width="100" height="50">
                    <v-icon
                      class="mt-2"
                      color="var(--color-accent-blue)"
                      size="34"
                    >
                      mdi-video-off
                    </v-icon>
                  </v-img>
                  <div
                    class="video-thumbnail-title px-1"
                    :class="{
                      'white--text': isSelected,
                    }"
                  >
                    {{ thumbnail.title }}
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </template>
        </v-hover>
      </v-slide-group-item>
    </v-slide-group>
  </v-sheet>
</template>

<script>
import { CameraIntegration } from '../helpers/camera/base';
import { get } from '../helpers/securityService';

export default {
  name: 'ThumbnailSlider',
  props: {
    camera: {
      type: CameraIntegration,
      required: true,
    },
    selectedCamera: {
      type: [Number, String],
    },
    disableErrorMessage: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change', 'loaded'],
  model: {
    prop: 'selectedCamera',
    event: 'change',
  },
  data() {
    return {
      loading: false,
      error: null,
      thumbnails: [],
      pinnedThumbnail: {},
      loadingTimeout: false,
      selectedThumbnail: null,
    };
  },
  methods: {
    async fetchThumbnails() {
      this.loading = true;
      try {
        let fetchedThumbnails = await this.camera.fetchThumbnails();
        this.thumbnails = fetchedThumbnails.map((thumbnail) => ({
          ...thumbnail,
          loadingTimeout: false,
        }));
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    async getPinnedThumbnail() {
      let pinnedThumbnailId = this.thumbnails[0]?.id;
      let location = this.camera.camera.location;
      try {
        const response = await get('security/camera_mapping', {
          location,
          orgId: this.camera.orgId,
        });

        if (!response) {
          return;
        }

        const map = JSON.parse(response);

        const rawRegister = this.camera?.event?.register;
        // Normalize register (handles number, string formats like 'POS0013', objects, etc.)
        const normalizeRegister = (val) => {
          if (val == null) return null;
          if (typeof val === 'object') {
            if ('value' in val) return normalizeRegister(val.value);
            if ('register' in val) return normalizeRegister(val.register);
          }
            if (typeof val === 'number') return val;
            if (typeof val === 'string') {
              const digits = val.replace(/[^0-9]/g, '');
              if (digits.length) return parseInt(digits, 10);
              const parsed = parseInt(val, 10);
              return Number.isNaN(parsed) ? null : parsed;
            }
            return null;
        };
        const register = normalizeRegister(rawRegister);
        let matchingCameraId = null;
        if (register != null) {
          matchingCameraId = Object.keys(map).find((cameraId) => {
            try {
              const arr = map[cameraId];
              if (!Array.isArray(arr)) return false;
              return arr.map(v => typeof v === 'string' ? parseInt(v,10) : v).includes(Number(register));
            } catch (_) { return false; }
          });
        }
        // If we found a matching cameraId, set the pinned thumbnail
        if (matchingCameraId) {
          const thumbnail = this.thumbnails.find(
            (thumbnail) => String(thumbnail.id) === String(matchingCameraId),
          );

          if (thumbnail) {
            this.pinnedThumbnail = thumbnail;
            pinnedThumbnailId = thumbnail.id;
          }
        }
      } catch (error) {
        console.error('Error setting camera mapping:', error);
      }

      this.toggleCamera(pinnedThumbnailId);
    },
    toggleCamera(id) {
      this.selectedThumbnail = id;
      this.$emit('change', id);
    },
    handleLoad(index) {
      this.thumbnails[index].loadingTimeout = false;
    },
    handleLoadFailure(index) {
      this.thumbnails[index].loadingTimeout = true;
    },
  },
  async mounted() {
    await this.fetchThumbnails();
    await this.getPinnedThumbnail();
    this.$emit('loaded');
  },
};
</script>

<style lang="scss" scoped>
.video-thumbnail-title {
  line-height: 1.5em;
  font-size: 11px;
  text-align: center !important;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-weight: 500;
}

.video-thumbnail-image-border {
  border: 1px solid var(--color-gray);
}

.pinned-thumbnail-badge {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 999;
}
.thumbnail-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.selected-thumbnail {
  border: 3px solid rgb(var(--v-theme-primary));
  background-color: rgb(var(--v-theme-primary));
  color: white;
}

.pinned-thumbnail {
  border: 3px solid blue;
  background-color: #e5e7eb;
}

.rounded {
  border-radius: 4px;
  border: 1px solid grey;
}
</style>
